import React, { useState, useEffect } from 'react';
import './PostBattleScreen.css';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Seed web descriptions for each secondary class
const SECONDARY_CLASS_SEED_INFO = {
  Fighter: 'No new seeds (physical specialist)',
  Mage: 'Access to the Mage seed web (Energy, Fire, Ice, Lightning, Wind, Earth, Water, Light, Dark)',
  Cleric: 'Access to the Cleric/Druid seed web (Good, Evil, Healing, Life, Death, Protection, etc.)',
  Thief: 'No new seeds (stealth specialist)',
  Druid: 'Access to the Cleric/Druid seed web (Life, Healing, Enhancing, etc.)',
  Psion: 'Access to the Psion seed web (Mentality, Strength, Agility, Vitality, Charisma)',
};

// When doubling down on your own class, describe the specialist benefits instead
const SPECIALIST_SEED_INFO = {
  Fighter: 'No new seeds — master your existing combat skills (multi-attack, DR, weapon mastery)',
  Mage: 'No new seeds — master your existing Mage seeds (+25% seed XP, Metamagic modifiers)',
  Cleric: 'No new seeds — master your existing Cleric seeds (+25% seed XP, enhanced healing)',
  Thief: 'No new seeds — master your existing stealth arts (backstab upgrades, evasion)',
  Druid: 'No new seeds — master your existing Druid seeds (companion upgrades, nature mastery)',
  Psion: 'No new seeds — master your existing Psion seeds (+25% seed XP, psionic focus)',
};

/**
 * AdvancedClassSelection — Non-dismissable modal for choosing an Advanced Class at level 40+.
 * The player picks a secondary class, which determines their AC (e.g., Fighter+Mage = Spellsword).
 */
const AdvancedClassSelection = ({ character, onCharacterUpdate, onClose }) => {
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player/advanced-class-options`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (response.ok) {
        if (data.alreadySelected) {
          onClose();
          return;
        }
        setOptions(data.options || []);
      } else {
        setError(data.error || 'Failed to load options');
      }
    } catch (err) {
      setError('Network error — try again');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (option) => {
    setSelectedOption(option);
    setConfirming(false);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!selectedOption) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/player/select-advanced-class`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ secondaryClass: selectedOption.secondaryClass })
      });

      const data = await response.json();
      if (response.ok) {
        await onCharacterUpdate();
        onClose();
      } else {
        setError(data.error || 'Failed to select Advanced Class');
        setIsSubmitting(false);
        setConfirming(false);
      }
    } catch (err) {
      setError('Network error — try again');
      setIsSubmitting(false);
      setConfirming(false);
    }
  };

  const formatStarterSeeds = (seeds) => {
    if (!seeds || Object.keys(seeds).length === 0) return 'None';
    return Object.entries(seeds).map(([name, xp]) => `${name} (${xp} XP)`).join(', ');
  };

  if (loading) {
    return (
      <div className="post-battle-overlay">
        <div className="post-battle-panel levelup" style={{ maxWidth: '700px' }}>
          <div className="pb-header levelup-header">
            <div className="pb-icon">⚔️</div>
            <h2>Loading Advanced Classes...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="post-battle-overlay">
      <div className="post-battle-panel levelup" style={{ maxWidth: '750px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="pb-header levelup-header">
          <div className="pb-icon">⚔️</div>
          <h2>Choose Your Advanced Class!</h2>
          <div className="level-badge" style={{ background: '#3a1a4a', borderColor: '#9060c0' }}>
            Level 40 — {character.class}
          </div>
        </div>

        <div className="ac-warning-box">
          ⚠️ Choose a secondary class to combine with your <strong>{character.class}</strong>. 
          This decision is <strong>permanent</strong> and shapes your abilities for the rest of the game!
        </div>

        <div className="ac-options-list">
          {options.map(opt => {
            const isSelected = selectedOption?.acName === opt.acName;
            const isSelf = opt.secondaryClass === character.class;
            return (
              <div
                key={opt.acName}
                className={`ac-option-card${isSelected ? ' selected' : ''}`}
                onClick={() => handleSelect(opt)}
              >
                <div className="ac-card-header">
                  <span className="ac-card-icon">{opt.icon}</span>
                  <span className={`ac-card-name${isSelected ? ' selected' : ''}`}>
                    {opt.name}
                  </span>
                  <span className="ac-card-combo">
                    ({character.class} + {opt.secondaryClass}{isSelf ? ' specialist' : ''})
                  </span>
                  {isSelected && <span className="ac-card-check">✓</span>}
                </div>
                <div className="ac-card-desc">
                  {opt.description}
                </div>
                <div className="ac-card-seeds">
                  <div>
                    <span className="ac-card-seeds-label">Seeds: </span>
                    <span className="ac-card-seeds-value">
                      {isSelf
                        ? SPECIALIST_SEED_INFO[opt.secondaryClass]
                        : SECONDARY_CLASS_SEED_INFO[opt.secondaryClass]}
                    </span>
                  </div>
                </div>
                {Object.keys(opt.starterSeeds || {}).length > 0 && (
                  <div className="ac-card-starter">
                    <span className="ac-card-starter-label">Starter Seeds: </span>
                    <span className="ac-card-starter-value">{formatStarterSeeds(opt.starterSeeds)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && <div className="pb-error" style={{ color: '#ff6060', textAlign: 'center', margin: '8px 0' }}>{error}</div>}

        {confirming && selectedOption && (
          <div className="ac-confirm-warning">
            ⚠️ Are you sure? You will become a <strong>{selectedOption.name}</strong>. 
            This cannot be undone! Click "Confirm" again to proceed.
          </div>
        )}

        <button
          className="pb-confirm-btn"
          onClick={handleConfirm}
          disabled={!selectedOption || isSubmitting}
          style={{
            background: confirming ? '#803030' : selectedOption ? '#306030' : '#333',
            borderColor: confirming ? '#c06060' : '#60c060',
            opacity: selectedOption ? 1 : 0.5,
          }}
        >
          {isSubmitting ? 'Selecting...' :
           confirming ? `Confirm: Become ${selectedOption?.name}` :
           selectedOption ? `Select ${selectedOption.name}` :
           'Choose an Advanced Class above'}
        </button>
      </div>
    </div>
  );
};

export default AdvancedClassSelection;
